import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { TriangleRight } from "lucide-react";

import TableComponent from "./TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import DetailsPopUp from "../view/DetailsPopup"; 
import { updateFilters, resetFilters } from "../redux/slices/filterSlice";
import { RootState } from "../redux/slices/store";
import { getData } from "../api/ApiServices";
import "../styles/table-search.css";
import "../styles/ItemModels.css";
import "../styles/modal.css";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/icon-style.css";
import "../styles/common/table-heading-icons.css";
import "../styles/StockSummaryStyles.css";

const TodayStocks: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const filters = useSelector((state: RootState) => state.filters);

  const [stockSummaryData, setStockSummaryData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [initializeStockSummary, isInitializeStockSummary] = useState<boolean>(false);

  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [selectedPartId, setSelectedPartId] = useState<string>("");

  const today = new Date().toISOString().split("T")[0];

  const columns = [
    {
      key: "partId",
      label: "Part No",
      nameKey: "internalPartNo",
      render: (value: any) =>
        value && typeof value === "object" ? value.internalPartNo || "N/A" : "N/A",
      sortable: true
    },
    {
      key: "partId",
      label: "Part Name",
      nameKey: "partName",
      render: (value: any) =>
        value && typeof value === "object" ? value.partName || "N/A" : "N/A",
      sortable: true
    },
    {
      key: "partId",
      label: "Model",
      nameKey: "model.name",
      render: (value: any) =>
        value && typeof value === "object" && value.model ? value.model.name || "N/A" : "N/A",
      sortable: true
    },
    {
      key: "partId",
      label: "Category",
      nameKey: "category.name",
      render: (value: any) =>
        value && typeof value === "object" && value.category ? value.category.name || "N/A" : "N/A",
      sortable: true
    },
    {
      key: "partId",
      label: "Uom",
      nameKey: "uom.name",
      render: (value: any) =>
        value && typeof value === "object" && value.uom ? value.uom.name || "N/A" : "N/A"
    },
    {
      key: "openingStock",
      label: "OpeningStock",
      sortable: true,
      isRightAlign: true
    },
    {
      key: "inwardQty",
      label: "Inward Qty",
      sortable: true,
      isRightAlign: true
    },
    {
      key: "outwardQty",
      label: "Outward Qty",
      sortable: true,
      isRightAlign: true
    },
    {
      key: "closingStock",
      label: "ClosingStock",
      sortable: true,
      isRightAlign: true,
      render: (value: number, rowData: any) => {
      
        const partIdValue = rowData.partId && rowData.partId._id;
        
        return (
          <div 
            className="stock-details-cell clickable"
            onClick={(e) => {
              e.stopPropagation();
              if (partIdValue) {
                handleClosingStockClick(partIdValue);
              }
            }}
            style={{ cursor: "pointer" }}
            title="Click to view details"
          >
            <span>{value}</span>
            {partIdValue && (
              <TriangleRight size={12} color="#808080" style={{ fill: '#808080' }} />
            )}
          </div>
        );
      }
    }
  ];

  const dateFormatDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const handleClosingStockClick = (partId: string) => {
    console.log("Clicked part ID for inward options API:", partId);
    setSelectedPartId(partId);
    setIsPopupOpen(true);
  };

  const fetchstockSummaryData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const queryParams: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        filter: filters,
        startdate: today,
        enddate: today
      };

      const response: any = await getData<{
        data: any[];
        total: number;
        pages: number;
      }>("/stocksummary", queryParams);
      console.log("API Response:", response);

      if (response && response.data && response.data.data) {
        setStockSummaryData(response.data.data);
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      } else {
        setStockSummaryData([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (err) {
      console.error("Error fetching stock data:", err);
      setError("Failed to load stock summary data. Please try again later.");
      setStockSummaryData([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderValue: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderValue);
  };

  useEffect(() => {
    dispatch(resetFilters());
    isInitializeStockSummary(true);
  }, [dispatch]);

  useEffect(() => {
    console.log("filters: ", filters);
    if (initializeStockSummary) {
      fetchstockSummaryData();
    }
  }, [initializeStockSummary, currentPage, itemsPerPage, sortBy, sortOrder, filters]);

  if (isLoading && stockSummaryData.length === 0) {
    return (
      <div className="item-models-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Stocks</span>
              <span>Today's Stock</span>
            </div>
          </div>
        </div>
      </div>

      <div>
        {error && <div className="stock-error-message">{error}</div>}

        <div className="box-container">
          <TableComponent
            columns={columns}
            data={stockSummaryData}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            excludeFields={["status", "grnDate"]}
            context="default"
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={totalItems}
          />
        </div>
      </div>
      
      {isPopupOpen && (
        <DetailsPopUp
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          partId={selectedPartId}
          startDate={today}
          endDate={today}
          isStockSummary={true}
        />
      )}
    </div>
  );
};

export default TodayStocks;